import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  User,
  Settings,
  Landmark,
  CreditCard,
  Store,
  Users,
  UsersRound,
  Construction,
  ShieldCheck,
  Key,
  Wallet
} from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import SuppliersTab from "../components/SuppliersTab";
import CustomersTab from "../components/CustomersTab";
import AccountTab from "../components/AccountTab";
import TeamTab from "../components/TeamTab";
import AdminTab from "../components/AdminTab";
import ApiKeysTab from "../components/ApiKeysTab";
import GeneralSettingsTab from "../components/GeneralSettingsTab";
import PaymentOptionsTab from "../components/PaymentOptionsTab";

const BASE_TABS = [
  { id: "account", label: "Account", icon: User, description: "Profile and security settings", hasContent: true },
  { id: "team", label: "Team", icon: UsersRound, description: "Manage team members and invitations", hasContent: true },
  { id: "algemene", label: "General Settings", icon: Settings, description: "Currency, notifications, defaults", hasContent: true },
  { id: "payment-options", label: "Payment Options", icon: Wallet, description: "Manage payment methods and terms", hasContent: true },
  { id: "bankrekeningen", label: "Bank Accounts", icon: Landmark, description: "Add/edit bank accounts for balance tracking" },
  { id: "creditcard", label: "Credit Card", icon: CreditCard, description: "Manage credit card accounts" },
  { id: "leveranciers", label: "Suppliers", icon: Store, description: "Supplier list for purchase invoices", hasContent: true },
  { id: "klanten", label: "Customers", icon: Users, description: "Customer list for sales invoices", hasContent: true },
];

const API_KEYS_TAB = { id: "api-keys", label: "API Keys", icon: Key, description: "Manage API keys for external integrations", hasContent: true };

const ADMIN_TAB = { id: "admin", label: "Admin", icon: ShieldCheck, description: "Manage all users in the system", hasContent: true };

const TabPlaceholder = ({ tab }) => {
  const Icon = tab.icon;
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4" data-testid={`tab-content-${tab.id}`}>
      <div className="bg-muted/50 border border-dashed border-muted-foreground/30 rounded-2xl p-8 max-w-md w-full">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
          <Icon className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold mb-2 uppercase tracking-wide text-muted-foreground">
          {tab.label}
        </h2>
        <p className="text-sm text-muted-foreground italic mb-4">
          {tab.description}
        </p>
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
          <Construction className="w-3 h-3" />
          Coming Soon
        </div>
      </div>
    </div>
  );
};

const TabContent = ({ tab }) => {
  switch (tab.id) {
    case "account":
      return <AccountTab />;
    case "team":
      return <TeamTab />;
    case "algemene":
      return <GeneralSettingsTab />;
    case "payment-options":
      return <PaymentOptionsTab />;
    case "leveranciers":
      return <SuppliersTab />;
    case "klanten":
      return <CustomersTab />;
    case "api-keys":
      return <ApiKeysTab />;
    case "admin":
      return <AdminTab />;
    default:
      return <TabPlaceholder tab={tab} />;
  }
};

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const TABS = user?.is_admin ? [...BASE_TABS, API_KEYS_TAB, ADMIN_TAB] : BASE_TABS;
  
  // Get initial tab from URL hash or default to first tab
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    const tab = TABS.find(t => t.id === hash);
    return tab ? tab.id : TABS[0].id;
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromHash);

  useEffect(() => { document.title = "Settings - Floatly"; }, []);

  // Update active tab when URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Save last active tab to localStorage
  useEffect(() => {
    localStorage.setItem('settings-last-tab', activeTab);
  }, [activeTab]);

  // On initial load, restore last tab if no hash
  useEffect(() => {
    if (!location.hash) {
      const savedTab = localStorage.getItem('settings-last-tab');
      if (savedTab && TABS.find(t => t.id === savedTab)) {
        navigate(`/settings#${savedTab}`, { replace: true });
      } else {
        navigate(`/settings#${TABS[0].id}`, { replace: true });
      }
    }
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/settings#${tabId}`, { replace: true });
  };

  const currentTab = TABS.find(t => t.id === activeTab) || TABS[0];

  return (
    <div className="min-h-screen flex flex-col" data-testid="settings-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your account and application preferences
          </p>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="border-b bg-white sticky top-0 z-30 shrink-0">
        <div className="px-6">
          <nav className="flex gap-1 overflow-x-auto scrollbar-hide -mb-px" role="tablist" data-testid="tab-navigation">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`panel-${tab.id}`}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 -mb-px",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <main className="flex-1 bg-muted/20">
        <div className={cn(
          "mx-auto py-8 px-6",
          currentTab.hasContent ? "max-w-5xl" : "max-w-4xl"
        )}>
          <div
            role="tabpanel"
            id={`panel-${currentTab.id}`}
            aria-labelledby={`tab-${currentTab.id}`}
            className="animate-in fade-in duration-200"
          >
            <TabContent tab={currentTab} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
